import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { forkJoin, Observable } from 'rxjs';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { FhirApiService } from '../../services/api/fhir-api.sevice';
import { DataCollectionFhirService } from '../data-collection.service';
import { DataFhirModel } from '../data.model';
import { CategoryElement } from '../partial/categories.model';

export interface DictionaryEntry extends CategoryElement {
  additionalValues?: {
    language?: string;
    use?: fhir4.Coding;
    value: string;
  }[];
  _resource: fhir4.ValueSetComposeIncludeConcept;
}
export interface Dictionary extends DataFhirModel<fhir4.ValueSet> {
  name?: string;
  description?: string;
  identifier?: string;
  list: DictionaryEntry[];
}

@Injectable({ providedIn: 'root' })
export class DictionariesCollectionService extends DataCollectionFhirService<Dictionary> {
  constructor(
    protected fhirApi: FhirApiService,
    public store: Store,
    private translocoService: TranslocoService,
  ) {
    super('dictionaries-collection', 'ValueSet', fhirApi);

    this.translocoService.langChanges$.subscribe(() => this.clear());
  }

  fromResource$(valueSet: fhir4.ValueSet): Observable<Dictionary> {
    const { name, description } = valueSet;

    const identifier = valueSet?.identifier?.find(
      ({ system }) => system === 'urn:ietf:rfc:3986',
    )?.value;

    const list = [];
    valueSet?.compose?.include.forEach(({ system, concept }) => {
      if (concept?.length) {
        concept.forEach((_resource: fhir4.ValueSetComposeIncludeConcept) => {
          const { code, display, designation } = _resource;
          if (code && display) {
            const item: DictionaryEntry = {
              id: code,
              name: display,
              _resource,
            };
            if (system) item.system = system;
            if (designation) item.additionalValues = designation;
            list.push(item);
          }
        });
      }
    });
    list.sort(function (a, b) {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    });

    return super.fromResource$(valueSet).pipe(
      map((baseItem) => ({
        ...baseItem,
        name,
        identifier,
        description,
        list,
      })),
    );
  }

  getListByIds$(ids: string[] = []): Observable<DictionaryEntry[]> {
    return this.translocoService.langChanges$.pipe(
      switchMap(() =>
        forkJoin(ids.map((id) => this.getItemById$(id))).pipe(
          defaultIfEmpty([] as Dictionary[]),
          map((dictionaries) => {
            return dictionaries.reduce(
              (acc, { list }) => [...acc, ...list],
              [],
            );
          }),
        ),
      ),
    );
  }
}
